import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import {
  buildMemberEyeView,
  type CashbackRow,
  type CashoutRow,
  type MemberEyeView,
} from './impersonationModel';
import type { Merchant, Placement, User } from '../dataProvider/types';

/**
 * The member's-eye reconstruction, read through the aggregation seam (`buildMemberEyeView`),
 * NOT computed in the component. It pulls the subject and the MEMBER-FACING projections the
 * provider already scrubs (the no-margin `cashbacks` read model, the no-payout-details
 * `cashouts` read model), plus the market offers, and assembles them. READ-ONLY: it only
 * ever reads; nothing here writes.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
};

export const useMemberEyeView = (
  subjectUserId: number,
): { view: MemberEyeView | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['member-eye-view', subjectUserId],
    queryFn: async () => {
      // Fetch the subject first; the market for the offers comes from the member.
      const user = (await dataProvider.getOne('users', { id: subjectUserId }))
        .data as unknown as User;
      const [cashbacks, cashouts, placements, merchants] = await Promise.all([
        dataProvider.getList('cashbacks', {
          ...ALL,
          filter: { userId: subjectUserId },
        }),
        dataProvider.getList('cashouts', {
          ...ALL,
          filter: { userId: subjectUserId },
        }),
        dataProvider.getList('placements', {
          ...ALL,
          filter: { market: user.market },
        }),
        dataProvider.getList('merchants', { ...ALL, filter: {} }),
      ]);
      return buildMemberEyeView({
        user,
        cashbacks: cashbacks.data as unknown as CashbackRow[],
        cashouts: cashouts.data as unknown as CashoutRow[],
        placements: placements.data as unknown as Placement[],
        merchants: merchants.data as unknown as Merchant[],
      });
    },
  });

  return { view: data ?? null, isLoading };
};
